import type React from 'react';
import { PropsWithChildren, useCallback } from 'react';

import {
  FileTextIcon,
  FileCodeIcon,
  FileIcon,
  FileTypeIcon,
  ImageIcon,
  Trash2Icon,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ACCEPTED_IMAGE_FORMATS } from '@/util/constants';

import EmptyState from './EmptyState';
import { useFileUpload } from './useFileUpload';

// Define the type for the format mapping (MIME type to file extensions)
export type AcceptedFormats = Record<string, string[]>;

type FileUploadProps = {
  onUpload: (file: File) => Promise<void>;
  maxFiles?: number;
  maxSizeMB?: number;
  /**
   * Map of MIME types to their extensions, e.g. { 'image/jpeg': ['.jpg', '.jpeg'] }
   * If not provided, defaults to ACCEPTED_IMAGE_FORMATS
   */
  acceptedFormats?: AcceptedFormats;
  /**
   * Callback function called when files are added or removed
   */
  onFilesChange?: (files: File[]) => void;
  /**
   * When provided, the component operates in controlled mode
   */
  files?: File[];

  /**
   * Callback function called when the upload button is clicked
   */
  onUploadClick?: () => Promise<void>;

  /**
   * Array of file names that are not allowed to be uploaded
   */
  invalidFileNames?: string[];

  /**
   * Placeholder text for the dropzone prompt
   */
  placeholder?: string;

  /**
   * Whether the dropzone lists the accepted file extensions
   */
  showAcceptedFormats?: boolean;
};

const FILE_ICON_CLASSES = 'h-4 w-4 text-muted-foreground' as const;

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';

  switch (extension) {
    case 'pdf':
    case 'doc':
    case 'docx':
      return <FileIcon className={FILE_ICON_CLASSES} />;
    case 'html':
    case 'md':
      return <FileCodeIcon className={FILE_ICON_CLASSES} />;
    case 'txt':
      return <FileTextIcon className={FILE_ICON_CLASSES} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
      return <ImageIcon className={FILE_ICON_CLASSES} />;
    default:
      return <FileTypeIcon className={FILE_ICON_CLASSES} />;
  }
};

const FileUpload = ({
  onUpload,
  maxSizeMB = 10,
  maxFiles,
  acceptedFormats = ACCEPTED_IMAGE_FORMATS,
  onFilesChange,
  files: controlledFiles,
  invalidFileNames,
  children,
  onUploadClick,
  placeholder,
  showAcceptedFormats,
}: PropsWithChildren<FileUploadProps>) => {
  const { t } = useTranslation();
  const {
    filesWithPreviews,
    files,
    error,
    uploadStatus,
    acceptedExtensions,
    handleFiles,
    removeFile,
    startUpload,
    completeUpload,
    failUpload,
  } = useFileUpload({
    maxSizeMB,
    acceptedFormats,
    onFilesChange,
    controlledFiles,
    invalidFileNames,
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    accept: Object.fromEntries(Object.keys(acceptedFormats).map((type) => [type, []])),
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: true,
    disabled: uploadStatus === 'uploading',
    maxFiles,
  });

  const handleUploadClick = useCallback(async () => {
    if (files.length === 0) {
      return;
    }
    startUpload();
    try {
      await onUploadClick?.();
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await onUpload(file);

        // Add 1000ms delay between uploads (except for the last file)
        if (i < files.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
      completeUpload();
    } catch (err) {
      if (err instanceof Error) {
        failUpload(err.message);
      } else {
        failUpload('Upload failed');
      }
    }
  }, [files, onUploadClick, startUpload, completeUpload, onUpload, failUpload]);

  const handleRemoveFile = useCallback(
    (e: React.MouseEvent, fileId: string) => {
      e.stopPropagation();
      removeFile(fileId);
    },
    [removeFile]
  );

  const isUploading = uploadStatus === 'uploading';

  return (
    <div className="flex flex-col">
      <EmptyState
        {...getRootProps()}
        status={uploadStatus}
        isDragActive={isDragActive}
        acceptedExtensions={acceptedExtensions}
        showAcceptedFormats={showAcceptedFormats}
        maxSizeMB={maxSizeMB}
        placeholder={placeholder}
        fileCount={files.length}
        disabled={isUploading}
      >
        <input {...getInputProps()} />
      </EmptyState>

      {error && !isUploading && <div className="mt-2 text-sm text-warning">{error}</div>}

      {files.length > 0 && (
        <ul
          className={cn('mt-4 flex max-h-40 flex-col gap-1 overflow-y-auto', {
            'cursor-not-allowed opacity-50': isUploading,
          })}
        >
          {filesWithPreviews.map((fileWithPreview) => (
            <li
              key={fileWithPreview.id}
              className="group flex items-center gap-2 rounded p-1 hover:bg-accent"
            >
              <div className="flex min-w-0 flex-1 items-center gap-1">
                {fileWithPreview.isImage && fileWithPreview.preview ? (
                  <img
                    src={fileWithPreview.preview}
                    alt={fileWithPreview.file.name}
                    className="size-4 shrink-0 rounded object-cover"
                  />
                ) : (
                  <span className="shrink-0">{getFileIcon(fileWithPreview.file.name)}</span>
                )}
                <span className="truncate text-xs leading-4 font-medium text-muted-foreground">
                  {fileWithPreview.file.name}
                </span>
              </div>

              {/* Remove button for file list */}
              <button
                className="shrink-0 rounded-sm text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                onClick={(e) => handleRemoveFile(e, fileWithPreview.id)}
                aria-label={`${t('common.remove')} ${fileWithPreview.file.name}`}
              >
                <Trash2Icon size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {children && <div className="mt-6">{children}</div>}

      {files.length > 0 && (
        <div className="mt-6 flex justify-end">
          <Button onClick={handleUploadClick} size="sm" disabled={isUploading}>
            {t('common.done')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
